import Vue from 'vue';
import Vuex from 'vuex';
import marked from 'marked';
import DOMPurify from 'dompurify';
import {queryApi, getApi} from '@/services/api.service';
import {parseToken} from '@/services/jwt.service';
import UserService from '@/services/user.service';
import OrganizationService from '@/services/organization.service';
import TagService from '@/services/tag.service';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    currentUser: null,
    currentMember: null,
    defaultOrganization: null,
    currentRiskStatus: null,
    currentRiskStatusReference: null,
    currentHealthStatus: null,
    currentHealthStatusActivity: null,
    currentCourse: null,
    currentActivities: null,
    currentActivityMembers: null,
    currentSubmissions: null,
    userAlerts: {},
    sessionUuid: null,
    alerts: null,
    unreadAlertCount: 0,
    perPage: 200,
  },
  mutations: {
    setCurrentUser(state, user) {
      state.currentUser = user;
    },
    setCurrentMember(state, member) {
      state.currentMember = member;
    },
    setDefaultOrganization(state, organization) {
      state.defaultOrganization = organization;
    },
    setCurrentRiskStatus(state, riskStatus) {
      state.currentRiskStatus = riskStatus;
    },
    setCurrentRiskStatusReference(state, riskStatusReference) {
      state.currentRiskStatusReference = riskStatusReference;
    },
    setUserAlerts(state, userAlerts) {
      // map the user alerts to user
      const userAlertsMapped = {
        [`currentUser_${state.currentUser.data.attributes.uuid}`]: userAlerts,
      };
      localStorage.setItem(`userAlerts_${state.currentUser.data.id}_${state.defaultOrganization.data.id}`, JSON.stringify(userAlertsMapped));
      localStorage.setItem(`alerts_${state.currentMember.attributes.uuid}`, JSON.stringify(userAlerts));
      state.userAlerts = userAlertsMapped;
    },
    setCurrentHealthStatus(state, healthStatus) {
      state.currentHealthStatus = healthStatus;
    },
    setCurrentHealthStatusActivity(state, healthStatusActivity) {
      state.currentHealthStatusActivity = healthStatusActivity;
    },
    setCurrentActivities(state, activities) {
      state.currentActivities = activities;
    },
    setCurrentActivityMembers(state, members) {
      state.currentActivityMembers = members;
    },
    setCurrentSubmissions(state, submissions) {
      state.currentSubmissions = submissions;
    },
    setCurrentCourse(state, course) {
      state.currentCourse = course;
    },
    removeCurrentUserAlert(state, userAlertIndex) {
      const currentUserAlerts = state.userAlerts[`currentUser_${state.currentUser.data.attributes.uuid}`];
      if (currentUserAlerts) {
        currentUserAlerts.splice(userAlertIndex, 1);
        // map the user alerts to user
        const userAlertsMapped = {
          [`currentUser_${state.currentUser.data.attributes.uuid}`]: currentUserAlerts,
        };
        localStorage.setItem(`userAlerts_${state.currentUser.data.attributes.uuid}`, JSON.stringify(userAlertsMapped));
        state.userAlerts = JSON.parse(localStorage.getItem(`userAlerts_${state.currentUser.data.attributes.uuid}`));
      }
    },
    setAlerts(state, alerts) {
      state.alerts = alerts;
    },
    setUnreadAlertCount(state, unreadAlertCount) {
      state.unreadAlertCount = unreadAlertCount;
    },
    addCurrentSubmission(state, submission) {
      state.currentSubmissions.data.push(submission);
    },
    updateActivityMember(state, payload) {
      state.currentActivityMembers.data.splice(payload.activityMemberIndex, 1, payload.activityMember);
    },
    setSessionUuid(state, sessionUuid) {
      if (sessionUuid) {
        localStorage.setItem('learner_app_session_uuid', sessionUuid);
      } else {
        localStorage.removeItem('learner_app_session_uuid');
      }
      state.sessionUuid = sessionUuid;
    },
  },
  getters: {
    currentUserAlerts(state) {
      let result = null;
      let userAlerts = null;
      const currentUser = state.currentUser;
      const defaultOrganization = state.defaultOrganization;
      if (currentUser && currentUser.data) {
        if (defaultOrganization && defaultOrganization.data) {
          userAlerts = JSON.parse(localStorage.getItem(`userAlerts_${state.currentUser.data.attributes.uuid}`));
          if (userAlerts) {
            result = userAlerts ? state.userAlerts[`currentUser_${state.currentUser.data.attributes.uuid}`] : null;
          }
        }
      }
      return result;
    },
    termsOfService(state) {
      return DOMPurify.sanitize(marked(state.defaultOrganization.data.attributes.terms_of_service || ''));
    },
    privacyPolicy(state) {
      return DOMPurify.sanitize(marked(state.defaultOrganization.data.attributes.privacy_policy || ''));
    },
  },
  actions: {
    getCurrentUser({commit}) {
      const token = parseToken();
      if (token && Object.prototype.hasOwnProperty.call(token, 'sub')) {
        return getApi(`users/${token.sub}`)
          .then(({data}) => {
            commit('setCurrentUser', data);
            return data;
          });
      }
      return null;
    },
    getDefaultOrganization({commit}) {
      const token = parseToken();
      const defaultOrgId = localStorage.getItem('defaultOrganizationId');
      if (defaultOrgId) {
        return getApi(`organizations/${defaultOrgId}`)
          .then(({data}) => {
            commit('setDefaultOrganization', data);
            return data;
          });
      }
      const params = {
        per_page: 1,
        'query[v2_members_user_id_eq]': token.sub,
        'query[s]': 'v2_members_default DESC',
      };
      return queryApi('organizations', params)
        .then(({data}) => {
          commit('setDefaultOrganization', data.data[0]);
          return data.data[0];
        });
    },
    getSessionUuid({commit}) {
      const sessionUuid = localStorage.getItem('learner_app_session_uuid');
      if (sessionUuid) {
        commit('setSessionUuid', sessionUuid);
      }
    },
    getCurrentMember({commit, state}) {
      const params = {
        per_page: 1,
        'query[memberable_type_eq]': 'V2::Organization',
        'query[memberable_id_eq]': state.defaultOrganization.data.id,
        'query[userable_id_eq]': state.currentUser.data.id,
        'query[userable_type_eq]': 'User',
      };
      return queryApi('members', params)
        .then(({data}) => {
          commit('setCurrentMember', data.data[0]);
          return data;
        });
    },
    getCurrentHealthCheck({commit, state}) {
      return OrganizationService.healthActivity(state.defaultOrganization.data.id)
        .then(({data}) => {
          commit('setCurrentHealthStatusActivity', data);
          const userId = state.currentUser.data.id;
          const organizationId = state.defaultOrganization.data.id;
          return UserService.healthStatus(userId, organizationId);
        }).then(({data}) => {
          commit('setCurrentHealthStatus', data);
          return data;
        });
    },
    getCurrentCourse({commit, state}) {
      const organizationId = state.defaultOrganization.data.id;
      const userId = state.currentUser.data.id;
      const params = {
        'query[category_eq]': 'Course',
        per_page: 1, // At some point we'll add multiple courses maybe
      };
      return UserService.groups(userId, organizationId, params)
        .then(({data}) => {
          commit('setCurrentCourse', data[0]);
        });
    },
    getCurrentActivities({commit, state}) {
      const userId = state.currentUser.data.id;
      const secondsSinceEpoch = Math.round(Date.now() / 1000);
      const params = {
        timestamp: secondsSinceEpoch,
        per_page: 250,
      };
      return UserService.activities(userId, state.defaultOrganization.data.id, params).then((data) => {
        commit('setCurrentActivities', data);
        return data;
      });
      // const groupParams = {
      //   'query[v2_group_id_eq]': state.currentCourse.id,
      //   'query[groupable_type_eq]': 'V2::Activity',
      //   'query[sorts]': 'created_at DESC',
      //   per_page: 50,
      // };
      // return queryApi('group_references', groupParams)
      //   .then(({data}) => {
      //     const activityIds = data.data.map((x) => x.relationships.groupable.data.id);
      //
      //     const activityParams = {
      //       'query[id_in]': activityIds,
      //       'query[sorts]': 'sequence ASC', // Not shown anywhere in the payload
      //       per_page: 100,
      //     };
      //
      //     return queryApi('activities', activityParams);
      //   })
      //   .then(({data}) => {
      //     commit('setCurrentActivities', data);
      //     return data;
      //   });
    },
    getCurrentSubmissionsForActivities({commit, state}) {
      const userId = state.currentUser.data.id;
      const secondsSinceEpoch = Math.round(Date.now() / 1000);
      const memberParams = {
        'query[memberable_type_eq]': 'V2::Activity',
        timestamp: secondsSinceEpoch,
        per_page: 250,
      };
      return UserService.members(userId, memberParams).then((data) => {
        commit('setCurrentActivityMembers', data);
        return UserService.submissions(userId, state.defaultOrganization.data.id, {timestamp: secondsSinceEpoch, per_page: 250});
      })
        .then((data) => {
          commit('setCurrentSubmissions', data);
          return data;
        });
      // const activityIds = state.currentActivities.data.map((x) => x.id);
      // const memberParams = {
      //   'query[memberable_id_in]': activityIds,
      //   'query[memberable_type_eq]': 'V2::Activity',
      //   'query[user_id_eq]': state.currentUser.data.id,
      //   'query[sorts]': 'id DESC',
      //   per_page: 100,
      // };
      // return queryApi('members', memberParams)
      //   .then(({data}) => {
      //     commit('setCurrentActivityMembers', data);
      //     const membershipIds = data.data.map((x) => x.id);
      //
      //     const submissionParams = {
      //       'query[v2_member_id_in]': membershipIds,
      //       'query[v2_activity_id_in]': activityIds,
      //       per_page: 100,
      //     };
      //
      //     return queryApi('submissions', submissionParams);
      //   })
      //   .then(({data}) => {
      //     commit('setCurrentSubmissions', data);
      //     return data;
      //   });
    },
    getCurrentRiskStatus({commit, state}) {
      const organizationId = state.defaultOrganization.data.id;
      return OrganizationService.currentRiskStatus(organizationId)
        .then(({data}) => {
          commit('setCurrentRiskStatusReference', data);
          return TagService.show(data.relationships.tag.data.id);
        })
        .then(({data}) => {
          commit('setCurrentRiskStatus', data);
          return data;
        });
    },
    // Grabs all the information needed in this big chalk of info
    getCurrentUserInfo({dispatch, state}) {
      return Promise.all([
        dispatch('getCurrentUser'),
        dispatch('getDefaultOrganization'),
      ]).then(() => dispatch('getCurrentMember'))
        .then(() => Promise.all([
          dispatch('getCurrentHealthCheck'),
          dispatch('getCurrentRiskStatus'),
          dispatch('getCurrentCourse'),
          dispatch('getSessionUuid'),
        ]))
        .then(() => dispatch('getCurrentActivities'))
        .then(() => dispatch('getCurrentSubmissionsForActivities'))
        .then(() => {
          // setup honeybadger vue context
          if (process.env.VUE_APP_ENV !== 'development') {
            Vue.$honeybadger.setContext({
              userId: state.currentUser.data.id,
              userEmail: state.currentUser.data.attributes.email,
              currentOrganization: state.defaultOrganization.data,
              currentMember: state.currentMember,
            });
          }
        });
    },
    clearAllGlobals({context}) {
      context.commit('setCurrentUser', null);
      context.commit('setCurrentMember', null);
      context.commit('setDefaultOrganization', null);
      context.commit('setCurrentRiskStatus', null);
      context.commit('setCurrentRiskStatusReference', null);
      context.commit('setUserAlerts', {});
      context.commit('setCurrentHealthStatus', null);
      context.commit('setCurrentHealthStatusActivity', null);
      context.commit('setCurrentActivities', null);
      context.commit('setCurrentActivityMembers', null);
      context.commit('setCurrentSubmissions', null);
      context.commit('setCurrentCourse', null);
      context.commit('setSessionUuid', null);
      context.commit('setAlerts', null);
      context.commit('setUnreadAlertCount', 0);
    },
  },
  modules: {},
});
